import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { User, Calendar, Activity, Lock, Save, X, Eye, EyeOff } from 'lucide-react';
import axiosInstance from '../Utils/axiosInstance';
import { TicketsService } from '@services/ticketsService.js';
import { CognitoAuthentication } from '../cognito/cognito-helper.js';
import { validatePassword } from '../Utils/helpers.js';
import { updateUserInfo } from '../APIs/usersAPIs.js';

import { getText, AppLocalEnum } from '../translate/text';

import { ProfileSettingsService } from '@services/profileSettingsService.js';

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'availability', label: 'Availability', icon: Calendar },
  { id: 'security', label: 'Security', icon: Lock },
];

const ProfileSettingsPage = () => {
  const { user } = useAuthStore();
  const { setLoading, addNotification } = useUIStore();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);

  const [language, setlanguage] = useState(localStorage.getItem('language') || AppLocalEnum.EN);
  useEffect(() => {
    setlanguage(localStorage.getItem('language') || AppLocalEnum.EN);
  }, [localStorage.getItem('language')]);

  // State Management
  const [activeTab, setActiveTab] = useState('profile');
  const [isHealthcareProvider, setIsHealthcareProvider] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  // Form Data
  const [profileData, setProfileData] = useState({
    displayName: '',
    notes: '',
    mobile_number: '',
  });

  const handleVerifyOtp = async () => {
    try {
      const result = await ProfileSettingsService.verifyOTP({
        identifier: profileData.mobile_number,
        password: otp,
      });
      if (result?.body?.valid) {
        try {
          const addResult = await ProfileSettingsService.addMobileNumber({
            mobile_number: profileData.mobile_number,
          });

          if (addResult?.data?.mobile_number === profileData.mobile_number) {
            addNotification({
              type: 'success',
              message: getText('mobile_number_added_successfully'),
            });
            setTimeout(() => {
              window.location.reload();
            }, 500);
          } else {
            addNotification({
              type: 'error',
              message: getText('failed_to_add_mobile_number'),
            });
          }
        } catch (error) {
          console.error('Error adding mobile number:', error);
          addNotification({
            type: 'error',
            message: getText('an_error_occured_while_adding_your_mobile_number'),
          });
        }
      } else {
        addNotification({
          type: 'error',
          message: getText('invalid_or_expired_otp'),
        });
      }
    } catch (error) {
      console.error('OTP Verification Failed:', error);
      addNotification({
        type: 'error',
        message: getText('an_error_occured_during_otp_verfication'),
      });
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await TicketsService.fetchUserRole();

        setProfileData({
          displayName: response.data.name,
          notes: response.data.notes,
          mobile_number: response.data.mobile_number,
        });
      } catch (err) {
        console.error('Error loading tickets:', err);
      }
    };
    loadUser();
  }, []);

  const [availabilitySettings, setAvailabilitySettings] = useState({
    workingHours: {
      monday: { checked: false, start: '09:00', end: '17:00' },
      tuesday: { checked: false, start: '09:00', end: '17:00' },
      wednesday: { checked: false, start: '09:00', end: '17:00' },
      thursday: { checked: false, start: '09:00', end: '17:00' },
      friday: { checked: false, start: '09:00', end: '17:00' },
      saturday: { checked: false, start: '09:00', end: '17:00' },
      sunday: { checked: false, start: '09:00', end: '17:00' },
    },
    breakTime: { start: '13:00', end: '14:00' },
    busyDates: [],
  });

  useEffect(() => {
    axiosInstance
      .get('/doctorsappuser/availability?action=view_availability', {
        headers: {
          Authorization: `${localStorage.getItem('token')}`,
          lang: language,
        },
      })
      .then((res) => {
        setIsHealthcareProvider(res.data.isHealthcareProvider);
        if (res.data.isHealthcareProvider && res.data.availability != null) {
          const busyDates = res.data.availability.busyDates.map((date) =>
            convertDateToDateOnly(date)
          );
          res.data.busyDates = busyDates;
          setAvailabilitySettings(res.data.availability);
        }
      })
      .catch((err) => {
        console.log('err', err);
      });
  }, []);

  // Security Settings
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const passwordCriteria = validatePassword(securityData.newPassword);

  // Load user data
  // const loadUserData = async () => {
  //   try {
  //     if (!user) return;

  //     const userDoc = await getDoc(doc(db, 'users', user.uid));
  //     if (userDoc.exists()) {
  //       const userData = userDoc.data();

  //       setProfileData({
  //         displayName: userData.displayName || user.displayName || '',
  //         notes: userData.notes || '',
  //       });

  //       // if (isHealthcareProvider && userData.availability) {
  //       //   setAvailabilitySettings(userData.availability);
  //       // }
  //     }
  //   } catch (error) {
  //     console.error('Error loading user data:', error);
  //     addNotification({
  //       type: 'error',
  //       message: 'Failed to load user data',
  //     });
  //   }
  // };

  // Load activity logs
  const loadRecentActivity = async () => {
    try {
      if (!user) return;

      const activityRef = collection(db, 'audit_logs');
      const q = query(
        activityRef,
        where('performedBy', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(10)
      );

      const snapshot = await getDocs(q);
      setRecentActivity(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    } catch (error) {
      console.error('Error loading activity:', error);
    }
  };

  // // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateUserInfo({
        name: profileData.displayName,
        notes: profileData.notes,
        mobile_number: profileData.mobile_number,
      });
      addNotification({
        type: 'success',
        message: getText('profile_updated_successfully'),
      });
    } catch (error) {
      addNotification({
        type: 'error',
        // message: replcaeTextToArabic(error.message) || replcaeTextToArabic('Failed to update profile'),
        message: getText('failed_to_update_profile'),
      });
    } finally {
      setLoading(false);
    }
  };

  function convertDateToLongFormate(dateString) {
    const date = new Date(dateString); // Create Date object
    return date.toISOString(); // Convert to ISO format
  }
  function convertDateToDateOnly(isoString) {
    return isoString.split('T')[0]; // Extract only the date part
  }

  // Update availability
  const handleUpdateAvailability = async (e) => {
    e.preventDefault();
    if (!isHealthcareProvider) return;

    setLoading(true);
    try {
      // await updateDoc(doc(db, 'users', user.uid), {
      //   availability: availabilitySettings,
      //   updatedAt: serverTimestamp(),
      // });

      const availabilitySettingsData = {
        workingHours: availabilitySettings.workingHours,
        breakTime: availabilitySettings.breakTime,
        busyDates: availabilitySettings.busyDates.map((date) => convertDateToLongFormate(date)),
      };

      axiosInstance
        .put('/doctorsappuser/availability?action=update_availability', availabilitySettingsData, {
          headers: {
            Authorization: `${localStorage.getItem('token')}`,
            lang: language,
          },
        })
        .then((res) => {
          addNotification({
            type: 'success',
            message: getText('availability_updated_successfully'),
          });
        })
        .catch((err) => {
          console.log('err', err);
          addNotification({
            type: 'error',
            message: getText('failed_to_update_availability'),
          });
        });
    } catch (error) {
      console.error('Error updating availability:', error);
      addNotification({
        type: 'error',
        message: getText('failed_to_update_availability'),
      });
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const handleUpdatePassword = async () => {
    if (securityData.newPassword !== securityData.confirmPassword) {
      addNotification({
        type: 'error',
        message: getText('new_passwords_do_not_match'),
      });
      return;
    }

    setLoading(true);
    try {
      const res = await CognitoAuthentication().changePassword(
        securityData.currentPassword,
        securityData.newPassword
      );

      if (res) {
        addNotification({
          type: 'success',
          message: getText('password_updated_successfully'),
        });
        setSecurityData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        // message: replcaeTextToArabic(error.message) || replcaeTextToArabic('Failed to update password'),
        message: getText('failed_to_update_password'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8"
      style={{ direction: language === AppLocalEnum.AR ? 'rtl' : 'ltr' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {getText('settings')}
          </h1>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-12 md:col-span-3">
            <nav className="space-y-1">
              {tabs.map(
                (tab) =>
                  (tab.id !== 'availability' || isHealthcareProvider) && (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                                    w-full flex items-center px-4 py-3
                                    text-sm font-medium rounded-lg
                                    ${
                                      activeTab === tab.id
                                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                                    }
                                `}
                    >
                      <tab.icon
                        className={`w-5 h-5 ${language == AppLocalEnum.AR ? 'ml-3' : 'mr-3'}`}
                      />
                      {getText(tab.label.toLowerCase().replace(' ', '_'))}
                    </button>
                  )
              )}
            </nav>
          </div>

          {/* Content */}
          <div className="col-span-12 md:col-span-9">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
            >
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium">{getText('basic_information')}</h2>
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="space-y-4">
                      {/* Name Field */}
                      <div>
                        <label className="block text-sm font-medium">{getText('name')}</label>
                        <input
                          type="text"
                          value={profileData.displayName}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              displayName: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>

                      {/* Notes Field */}
                      <div>
                        <label className="block text-sm font-medium">{getText('notes')}</label>
                        <textarea
                          value={profileData.notes}
                          onChange={(e) =>
                            setProfileData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          rows={4}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          onClick={handleUpdateProfile}
                        >
                          <Save
                            className={`w-4 h-4 ${language == AppLocalEnum.AR ? 'ml-2' : 'mr-2'}`}
                          />
                          {getText('save_changes')}
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* === Mobile Number Section === */}
                  <div className="space-y-2 border-t pt-6">
                    <label className="block text-sm font-medium">{getText('mobile_number')}</label>

                    {!editingPhone ? (
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-gray-700">
                          {profileData.mobile_number || getText('no_mobile_provided')}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingPhone(true);
                            setOtpSent(false);
                            setOtp('');
                          }}
                          className="text-sm text-primary-600 hover:underline"
                        >
                          {getText('edit')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            value={profileData.mobile_number || ''}
                            onChange={(e) => {
                              const input = e.target.value;
                              if (/^\d{0,11}$/.test(input)) {
                                setProfileData((prev) => ({
                                  ...prev,
                                  mobile_number: input,
                                }));
                                setOtpSent(false);
                                setOtp('');
                              }
                            }}
                            placeholder={getText('enter_mobile')}
                            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${
                              localStorage.getItem('language') === 'ar' ? 'text-right' : ''
                            }`}
                            style={
                              localStorage.getItem('language') === 'ar'
                                ? { direction: 'rtl' }
                                : undefined
                            }
                          />
                          <button
                            type="button"
                            className="mt-1 px-3 py-1 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                            disabled={
                              !profileData.mobile_number || profileData.mobile_number.length !== 11
                            }
                            onClick={async () => {
                              setOtpSent(true);
                              await ProfileSettingsService.sendOTP({
                                identifier: profileData.mobile_number,
                              });
                            }}
                          >
                            {getText('send_otp')}
                          </button>
                        </div>

                        {otpSent && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                              placeholder={getText('enter_otp')}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                            <button
                              type="button"
                              className="mt-1 px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                              onClick={handleVerifyOtp}
                            >
                              {getText('verify')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Availability Tab */}
              {activeTab === 'availability' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium">{getText('availability_settings')}</h2>
                  <div className="space-y-6">
                    {/* Working Hours */}
                    <div>
                      <h3 className="text-sm font-medium mb-4">{getText('working_hours')}</h3>
                      <div className="space-y-4">
                        {Object.entries(availabilitySettings.workingHours).map(
                          ([day, hours]) =>
                            day !== '_id' && (
                              <div key={day} className="flex items-center space-x-4">
                                <div className="w-32">
                                  <label className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={hours.checked}
                                      onChange={() => {
                                        setAvailabilitySettings((prev) => ({
                                          ...prev,
                                          workingHours: {
                                            ...prev.workingHours,
                                            [day]: {
                                              ...hours,
                                              checked: !hours.checked,
                                            },
                                          },
                                        }));
                                      }}
                                      className={`form-checkbox ${language === AppLocalEnum.AR ? 'mx-2' : ''}`}
                                    />
                                    <span className="ml-2 capitalize">
                                      {getText(day.toLowerCase().replace(' ', ''))}
                                    </span>
                                  </label>
                                </div>
                                <input
                                  type="time"
                                  value={hours.start}
                                  onChange={(e) => {
                                    setAvailabilitySettings((prev) => ({
                                      ...prev,
                                      workingHours: {
                                        ...prev.workingHours,
                                        [day]: {
                                          ...hours,
                                          start: e.target.value,
                                        },
                                      },
                                    }));
                                  }}
                                  disabled={!hours.checked}
                                  className="form-input"
                                />
                                <span>{getText('to')}</span>
                                <input
                                  type="time"
                                  value={hours.end}
                                  onChange={(e) => {
                                    setAvailabilitySettings((prev) => ({
                                      ...prev,
                                      workingHours: {
                                        ...prev.workingHours,
                                        [day]: {
                                          ...hours,
                                          end: e.target.value,
                                        },
                                      },
                                    }));
                                  }}
                                  disabled={!hours.checked}
                                  className="form-input"
                                />
                              </div>
                            )
                        )}
                      </div>
                    </div>

                    {/* Break Time */}
                    <div>
                      <h3 className="text-sm font-medium mb-4">{getText('break_time')}</h3>
                      <div className="flex items-center space-x-4">
                        <input
                          type="time"
                          value={availabilitySettings.breakTime.start}
                          onChange={(e) => {
                            setAvailabilitySettings((prev) => ({
                              ...prev,
                              breakTime: {
                                ...prev.breakTime,
                                start: e.target.value,
                              },
                            }));
                          }}
                          className="form-input"
                        />
                        <span className={`${language === AppLocalEnum.AR ? 'mx-2' : ''}`}>
                          {getText('to')}
                        </span>
                        <input
                          type="time"
                          value={availabilitySettings.breakTime.end}
                          onChange={(e) => {
                            setAvailabilitySettings((prev) => ({
                              ...prev,
                              breakTime: {
                                ...prev.breakTime,
                                end: e.target.value,
                              },
                            }));
                          }}
                          className="form-input"
                        />
                      </div>
                    </div>

                    {/* Busy Dates */}
                    <div>
                      <h3 className="text-sm font-medium mb-4">{getText('busy_dates')}</h3>
                      <div className="space-y-4">
                        <div>
                          <input
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            onChange={(e) => {
                              if (e.target.value) {
                                setAvailabilitySettings((prev) => ({
                                  ...prev,
                                  busyDates: [...prev.busyDates, e.target.value],
                                }));
                              }
                            }}
                            className="form-input"
                          />
                        </div>
                        {availabilitySettings.busyDates.map((date, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded"
                          >
                            <span>{new Date(date).toLocaleDateString()}</span>
                            <button
                              onClick={() => {
                                setAvailabilitySettings((prev) => ({
                                  ...prev,
                                  busyDates: prev.busyDates.filter((_, i) => i !== index),
                                }));
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleUpdateAvailability}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Save
                          className={`w-4 h-4 ${language === AppLocalEnum.AR ? 'ml-2' : 'mr-2'}`}
                        />
                        {getText('save_availability')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium">{getText('change_password')}</h2>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdatePassword();
                    }}
                  >
                    <div className="space-y-4">
                      {/* Current Password */}
                      <div className="relative">
                        <label className="block text-sm font-medium">
                          {getText('current_password')}
                        </label>
                        <input
                          type={showCurrent ? 'text' : 'password'}
                          value={securityData.currentPassword}
                          onChange={(e) =>
                            setSecurityData((prev) => ({
                              ...prev,
                              currentPassword: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pr-10"
                          required
                        />
                        <span
                          onClick={() => setShowCurrent(!showCurrent)}
                          className="absolute right-3 top-9 cursor-pointer text-neutral-500"
                        >
                          {showCurrent ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </span>
                      </div>

                      {/* New Password */}
                      {/* New Password */}
                      <div className="relative">
                        <label className="block text-sm font-medium">
                          {getText('new_password')}
                        </label>
                        <input
                          type={showNew ? 'text' : 'password'}
                          value={securityData.newPassword}
                          onChange={(e) => {
                            setSecurityData((prev) => ({
                              ...prev,
                              newPassword: e.target.value,
                            }));
                          }}
                          onFocus={() => setShowPasswordPopup(true)}
                          onBlur={() => setShowPasswordPopup(false)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pr-10"
                          required
                        />
                        <span
                          onClick={() => setShowNew(!showNew)}
                          className="absolute right-3 top-9 cursor-pointer text-neutral-500"
                        >
                          {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </span>

                        {/* Password Criteria Pop-up */}
                        {showPasswordPopup && (
                          <div className="absolute z-10 mt-2 w-full p-4 border border-neutral-300 rounded-lg bg-neutral-50 shadow-sm text-sm space-y-1">
                            <p
                              className={
                                passwordCriteria.hasUpperCase
                                  ? 'text-green-600'
                                  : 'text-neutral-500'
                              }
                            >
                              ✔ {getText('uppercase_letter')}
                            </p>
                            <p
                              className={
                                passwordCriteria.hasLowerCase
                                  ? 'text-green-600'
                                  : 'text-neutral-500'
                              }
                            >
                              ✔ {getText('lowercase_letter')}
                            </p>
                            <p
                              className={
                                passwordCriteria.hasNumber ? 'text-green-600' : 'text-neutral-500'
                              }
                            >
                              ✔ {getText('at_least_one_number')}
                            </p>
                            <p
                              className={
                                passwordCriteria.hasSpecialChar
                                  ? 'text-green-600'
                                  : 'text-neutral-500'
                              }
                            >
                              ✔ {getText('special_character')}
                            </p>
                            <p
                              className={
                                passwordCriteria.hasLength ? 'text-green-600' : 'text-neutral-500'
                              }
                            >
                              ✔ {getText('eight_character_minimum_length')}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Confirm New Password */}
                      <div className="relative">
                        <label className="block text-sm font-medium">
                          {getText('confirm_new_password')}
                        </label>
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          value={securityData.confirmPassword}
                          onChange={(e) =>
                            setSecurityData((prev) => ({
                              ...prev,
                              confirmPassword: e.target.value,
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pr-10"
                          required
                        />
                        <span
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="absolute right-3 top-9 cursor-pointer text-neutral-500"
                        >
                          {showConfirm ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </span>
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <Save
                            className={`w-4 h-4 ${language == AppLocalEnum.AR ? 'ml-2' : 'mr-2'}`}
                          />
                          {getText('update_password')}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsPage;
